import "./tabs.scss";

import * as RadixTabs from "@radix-ui/react-tabs";
import cn from "clsx";
import { Icon } from "design-system/components/icons/icon";
import { Alignment, EditAttributes } from "design-system/types/types";
import { PointerEvent, ReactNode, useEffect, useRef, useState } from "react";

export interface TabProps {
  tabLabel: string;
  tabContent: ReactNode;
  tabContentPadding?: boolean;

  editAttributes?: {
    tabLabel?: EditAttributes;
  };
}

export interface TabsProps {
  tabs: Array<TabProps>;
  ariaLabel?: string;
  align?: Alignment;
  HeadingLevel?: "h2" | "h3" | "h4";
}

export const getTabHash = (label?: string) =>
  label ? label.replace(/[^A-Za-z0-9]/g, "") : "";

function onTabValueChange(value: string) {
  window.history.pushState({}, "", `#${value}`);

  const event = new CustomEvent("hashchange", {
    detail: value || "",
  });
  dispatchEvent(event);
}

/**
 * ## Implementation Notes
 * - This component utilizes the [Radix Tabs](https://www.radix-ui.com/docs/primitives/components/tabs) component
 *
 * ## See it in use on...
 * - The [event/conference page](/story/example-pages-secondary-page-types--story)
 *
 * - **`id: P-024-000-00`**
 */
export function Tabs({
  tabs,
  ariaLabel,
  align,
  HeadingLevel = "h2",
}: TabsProps) {
  const wrapperClass = cn(
    align && `hbs-global-align-${align} hbs-component--tabs`,
  );
  const navigationRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const prevHash = useRef<string>();
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const navigation = navigationRef.current;
    const list = listRef.current;
    if (!navigation || !list) return;

    const updateScrollState = () => {
      // Compare against the full width so the buttons disappear as soon as the
      // tabs would fit without them.
      setHasOverflow(list.scrollWidth > navigation.clientWidth + 1);
      setCanScrollLeft(list.scrollLeft > 1);
      setCanScrollRight(
        list.scrollLeft < list.scrollWidth - list.clientWidth - 1,
      );
    };

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(navigation);
    observer.observe(list);
    tabRefs.current.forEach((tab) => tab && observer.observe(tab));
    list.addEventListener("scroll", updateScrollState, { passive: true });
    updateScrollState();

    return () => {
      observer.disconnect();
      list.removeEventListener("scroll", updateScrollState);
    };
  }, [tabs]);

  const scrollTabs = (direction: -1 | 1) => {
    const list = listRef.current;
    if (!list) return;
    list.scrollBy({
      left: direction * list.clientWidth * 0.8,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  };

  // Keep focus on the current tab when a pointer click reaches a scroll edge.
  const preventScrollButtonFocus = (event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const focusHashedTab = () => {
    const hash = window.location.hash.replace("#", "");
    const focusedTab = tabRefs.current.find(
      (ref) => ref?.dataset.hash === hash,
    );
    if (focusedTab) {
      focusedTab.focus();
      const activeTab = tabRefs.current.find(
        (ref) => ref?.getAttribute("aria-selected") === "true",
      );
      const event = new CustomEvent("hashchange", {
        detail: focusedTab || activeTab?.dataset.hash,
      });
      if (!activeTab || prevHash.current !== activeTab?.dataset.hash) {
        prevHash.current = activeTab?.dataset.hash || "";
        dispatchEvent(event);
      }
    }
  };

  useEffect(() => {
    focusHashedTab();
    window.addEventListener("hashchange", focusHashedTab);
    return () => window.removeEventListener("hashchange", focusHashedTab);
  }, []);

  return (
    <div className={wrapperClass} data-region="p__tabs">
      <div className="hbs-tabs">
        <RadixTabs.Root
          className="hbs-tabs__wrapper"
          defaultValue={getTabHash(tabs[0]?.tabLabel)}
          onValueChange={onTabValueChange}
        >
          <div
            className={cn(
              "hbs-tabs__navigation",
              hasOverflow && "hbs-tabs__navigation--overflow",
              hasOverflow && canScrollLeft && "hbs-tabs__navigation--fade-left",
              hasOverflow &&
                canScrollRight &&
                "hbs-tabs__navigation--fade-right",
            )}
            ref={navigationRef}
          >
            <button
              type="button"
              className="hbs-tabs__scroll-button hbs-tabs__scroll-button--left"
              aria-label="Scroll tabs left"
              tabIndex={-1}
              disabled={!hasOverflow || !canScrollLeft}
              onPointerDown={preventScrollButtonFocus}
              onClick={() => scrollTabs(-1)}
            >
              <Icon name="caretRight" />
            </button>
            <RadixTabs.List
              className="hbs-tabs__trigger-list"
              aria-label={ariaLabel || "Tabs"}
              ref={listRef}
            >
              {tabs.map((tab, i) => {
                const value = getTabHash(tab?.tabLabel);
                return (
                  <RadixTabs.Trigger
                    {...tab.editAttributes?.tabLabel}
                    key={i + value}
                    value={value || ""}
                    data-hash={value}
                    className="hbs-tabs__tab-trigger"
                    ref={(ref) => {
                      tabRefs.current[i] = ref;
                    }}
                  >
                    <HeadingLevel className="hbs-tabs__tab-header">
                      {tab.tabLabel}
                    </HeadingLevel>
                  </RadixTabs.Trigger>
                );
              })}
            </RadixTabs.List>
            <button
              type="button"
              className="hbs-tabs__scroll-button"
              aria-label="Scroll tabs right"
              tabIndex={-1}
              disabled={!hasOverflow || !canScrollRight}
              onPointerDown={preventScrollButtonFocus}
              onClick={() => scrollTabs(1)}
            >
              <Icon name="caretRight" />
            </button>
          </div>

          {tabs.map((tab, i) => (
            <RadixTabs.Content
              key={i}
              value={getTabHash(tab?.tabLabel)}
              className={cn(
                "hbs-tabs__tab-content",
                tab.tabContentPadding && "hbs-tabs__tab-content--padding",
              )}
            >
              {tab.tabContent}
            </RadixTabs.Content>
          ))}
        </RadixTabs.Root>
      </div>
    </div>
  );
}
